/**
 * Настройки → Маршрутизация (ТЗ §5 и §8.7): 14 категорий мини, у каждой — исполнитель,
 * отделение и при необходимости переопределённый приоритет.
 *
 * Список категорий приходит с сервера ровно из 14 строк (`GET /api/settings/routing`),
 * поэтому фронт ничего не додумывает: категории — константа бэкенда `categoryMatrix.ts`.
 * Правки копятся локально и уходят одним `PATCH /api/settings/routing`.
 */

import { useEffect, useState } from 'react';
import { categoryLabel } from '@/lib/categories';
import type { DepartmentDto, RoutingPatchItem, RoutingRuleDto, UserDto } from '@/api/types';
import { errorMessage, FormError, LoadingState } from '@/components/ErrorState';

export interface RoutingTabProps {
    rules: RoutingRuleDto[];
    executors: UserDto[];
    departments: DepartmentDto[];
    loading?: boolean;
    error?: unknown;
    onSave: (items: RoutingPatchItem[]) => Promise<unknown>;
}

type Draft = Record<string, RoutingPatchItem>;

function toDraft(rules: RoutingRuleDto[]): Draft {
    const draft: Draft = {};
    for (const rule of rules) {
        draft[rule.category] = {
            category: rule.category,
            executor_id: rule.executor_id,
            department_id: rule.department_id,
            priority_override: rule.priority_override,
        };
    }
    return draft;
}

function sameItem(a: RoutingPatchItem, b: RoutingRuleDto): boolean {
    return (
        a.executor_id === b.executor_id &&
        a.department_id === b.department_id &&
        a.priority_override === b.priority_override
    );
}

export default function RoutingTab({
    rules,
    executors,
    departments,
    loading,
    error,
    onSave,
}: RoutingTabProps) {
    const rulesKey = rules
        .map(
            (rule) =>
                `${rule.category}:${rule.executor_id ?? ''}:${rule.department_id ?? ''}:${rule.priority_override ?? ''}`,
        )
        .join('|');

    const [draft, setDraft] = useState<Draft>(() => toDraft(rules));
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    // Пришли свежие правила с сервера — черновик начинается заново.
    useEffect(() => {
        setDraft(toDraft(rules));
        setSaved(false);
        // rulesKey меняется только вместе с содержимым правил.
    }, [rulesKey]);

    const patch = (category: string, change: Partial<RoutingPatchItem>) => {
        setSaved(false);
        setDraft((prev) => {
            const current = prev[category];
            if (!current) return prev;
            return { ...prev, [category]: { ...current, ...change } };
        });
    };

    const dirtyCount = rules.filter((rule) => {
        const item = draft[rule.category];
        return item ? !sameItem(item, rule) : false;
    }).length;

    const save = () => {
        if (busy || dirtyCount === 0) return;
        setBusy(true);
        setSaveError(null);
        void (async () => {
            try {
                await onSave(rules.map((rule) => draft[rule.category]).filter(Boolean));
                setSaved(true);
            } catch (cause) {
                setSaveError(errorMessage(cause));
            } finally {
                setBusy(false);
            }
        })();
    };

    const activeExecutors = executors.filter((user) => user.is_active);

    return (
        <>
            <div className="modal-note">
                Правило решает, кому уйдёт дело сразу после квалификации: панель находит строку по категории
                обращения и назначает исполнителя и отделение. Если исполнитель не задан, дело попадает в очередь
                «Без исполнителя» у главного врача. Приоритет по умолчанию задан для каждой категории — меняйте его
                только там, где клинике действительно нужен другой порядок. Уже заведённые дела не переназначаются.
            </div>

            <div className="stat-block">
                <div className="block-header">
                    <span>🧭 Категории обращений и маршруты</span>
                    <span className="checklist-progress">
                        {dirtyCount > 0 ? `изменено правил: ${dirtyCount}` : 'изменений нет'}
                    </span>
                </div>
                <div className="block-body">
                    <FormError>{error ? errorMessage(error) : null}</FormError>
                    {loading ? <LoadingState variant="inline" label="Загружаем правила маршрутизации…" /> : null}
                    {!loading && rules.length === 0 ? (
                        <div className="list-empty">Правила маршрутизации не заведены.</div>
                    ) : null}

                    {rules.map((rule) => {
                        const item = draft[rule.category];
                        if (!item) return null;
                        const hint = [
                            rule.default_dept ? `по матрице: ${rule.default_dept}` : null,
                            rule.default_priority ? `приоритет ${rule.default_priority}` : null,
                            rule.default_deadline_days ? `срок ${rule.default_deadline_days} дн.` : null,
                        ]
                            .filter(Boolean)
                            .join(' · ');

                        return (
                            <div className="check-item" key={rule.category}>
                                <span className="check-text">
                                    {categoryLabel(rule.category)}
                                    {hint ? (
                                        <>
                                            <br />
                                            <span className="my-appl">{hint}</span>
                                        </>
                                    ) : null}
                                </span>
                                <span className="review-wrap">
                                    <select
                                        className="act-select"
                                        value={item.executor_id ?? ''}
                                        aria-label={`Исполнитель категории «${categoryLabel(rule.category)}»`}
                                        onChange={(event) =>
                                            patch(rule.category, { executor_id: event.target.value || null })
                                        }
                                    >
                                        <option value="">— без исполнителя</option>
                                        {activeExecutors.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.post && user.post !== user.full_name ? `${user.full_name} · ${user.post}` : user.full_name}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="act-select"
                                        value={item.department_id ?? ''}
                                        aria-label={`Отделение категории «${categoryLabel(rule.category)}»`}
                                        onChange={(event) =>
                                            patch(rule.category, { department_id: event.target.value || null })
                                        }
                                    >
                                        <option value="">— без отделения</option>
                                        {departments.map((department) => (
                                            <option key={department.id} value={department.id}>
                                                {department.name}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="act-select"
                                        value={item.priority_override === null ? '' : String(item.priority_override)}
                                        aria-label={`Приоритет категории «${rule.category}»`}
                                        onChange={(event) =>
                                            patch(rule.category, {
                                                priority_override: event.target.value
                                                    ? Number(event.target.value)
                                                    : null,
                                            })
                                        }
                                    >
                                        <option value="">приоритет из матрицы</option>
                                        {[1, 2, 3, 4, 5].map((priority) => (
                                            <option key={priority} value={priority}>
                                                приоритет {priority}
                                            </option>
                                        ))}
                                    </select>
                                </span>
                            </div>
                        );
                    })}
                </div>
                <div className="totals-row">
                    <span>
                        Категорий: <b>{rules.length}</b> · без исполнителя:{' '}
                        <b>{rules.filter((rule) => !draft[rule.category]?.executor_id).length}</b>
                    </span>
                    <span>Перечень категорий общий для всех клиник и в панели не меняется.</span>
                </div>
            </div>

            <FormError>{saveError}</FormError>

            <div className="draft-bar">
                <button type="button" className="act-btn primary" disabled={busy || dirtyCount === 0} onClick={save}>
                    {busy ? 'Сохраняем…' : 'Сохранить маршрутизацию'}
                </button>
                <button
                    type="button"
                    className="act-btn"
                    disabled={busy || dirtyCount === 0}
                    onClick={() => {
                        setDraft(toDraft(rules));
                        setSaveError(null);
                    }}
                >
                    Отменить правки
                </button>
                <span className="draft-note">
                    {saved && dirtyCount === 0
                        ? 'Маршрутизация сохранена. Уже заведённые дела не переназначаются.'
                        : 'Изменения применяются к новым делам: уже назначенные дела остаются у своих исполнителей.'}
                </span>
            </div>
        </>
    );
}
